import { Text, Textarea, VStack } from '@chakra-ui/react'
import { ReactNode, useEffect, useState } from 'react'

import { ActionKind } from '../ActionButton/actions'
import { CharCount } from '../CharCount'
import { FormField } from '../FormField'
import { FormModal } from '../FormModal'

export interface IReasonModalProps {
  isOpen: boolean
  onClose: () => void
  title: string
  /** Rótulo do campo de texto: "Motivo da reprovação", "Motivo do cancelamento". */
  label: string
  /** Frase acima do campo, com a consequência da ação. */
  description?: ReactNode
  /** Cor do botão de confirmação, do registry `ACTIONS`. */
  confirmAction?: ActionKind
  confirmLabel?: string
  /** Para quando "Cancelar" ficaria ambíguo — cancelar uma RP, por exemplo. */
  cancelLabel?: string
  /** O mesmo `max` da validação da API. */
  maxLength: number
  /** Rejeitando, o modal fica aberto para tentar de novo; quem chama mostra o erro. */
  onConfirm: (reason: string) => Promise<unknown>
}

/**
 * Ação que só se confirma com um motivo escrito — reprovar, cancelar. Não é um `ConfirmDialog`
 * porque coleta texto: é um formulário de um campo só.
 */
export default function ReasonModal({
  isOpen,
  onClose,
  title,
  label,
  description,
  confirmAction = 'reject',
  confirmLabel,
  cancelLabel,
  maxLength,
  onConfirm
}: IReasonModalProps) {
  const [reason, setReason] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (isOpen) setReason('')
  }, [isOpen])

  const handleConfirm = async () => {
    setSaving(true)
    try {
      await onConfirm(reason.trim())
      onClose()
    } catch {
      // Kept open so the reason is not lost; the caller already showed the error.
    } finally {
      setSaving(false)
    }
  }

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      onSubmit={handleConfirm}
      size="compact"
      confirmAction={confirmAction}
      confirmLabel={confirmLabel}
      cancelLabel={cancelLabel}
      isLoading={saving}
      isConfirmDisabled={reason.trim() === ''}
      isDismissible={!saving}
    >
      <VStack align="stretch" spacing={4}>
        {description && <Text>{description}</Text>}
        <FormField label={label} isRequired>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={maxLength}
            rows={5}
            isDisabled={saving}
          />
          <CharCount value={reason} max={maxLength} />
        </FormField>
      </VStack>
    </FormModal>
  )
}
